import { ComponentType, LazyExoticComponent, lazy } from 'react';

type RouteComponent = ComponentType<Record<string, never>>;

/**
 * Extended lazy component type that includes preload method
 */
export interface PreloadableComponent<T extends RouteComponent>
  extends LazyExoticComponent<T> {
  preload: () => Promise<{ default: T }>;
}

export type EagerPreloadableComponent<T extends RouteComponent> = T & {
  preload: () => Promise<{ default: T }>;
};

export type RoutePreload = () => Promise<unknown> | void;

/**
 * Wrapper around React.lazy that exposes a preload method
 * Allows preloading route components on hover or during idle time
 * 
 * @example
 * const MortgagePage = lazyWithPreload(() => import('./pages/MortgagePage'));
 * 
 * // Later, preload on hover:
 * <Link onMouseEnter={() => MortgagePage.preload()}>Bolån</Link>
 */
export function lazyWithPreload<T extends RouteComponent>(
  factory: () => Promise<{ default: T }>
): PreloadableComponent<T> {
  let componentPromise: Promise<{ default: T }> | null = null;

  const load = () => {
    if (!componentPromise) {
      componentPromise = factory().catch((error) => {
        componentPromise = null;
        throw error;
      });
    }
    return componentPromise;
  };

  const LazyComponent = lazy(load) as PreloadableComponent<T>;
  LazyComponent.preload = load;

  return LazyComponent;
}

export function withResolvedPreload<T extends RouteComponent>(
  Component: T
): EagerPreloadableComponent<T> {
  return Object.assign(Component, {
    preload: () => Promise.resolve({ default: Component }),
  });
}

export function preloadRoute(preload?: RoutePreload) {
  if (!preload) return;
  void Promise.resolve()
    .then(() => preload())
    .catch(() => {
      // A hover/touch preload must never break the current page.
    });
}
